import { X } from "lucide-react";
import { useEffect, useState } from "react";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { DateRangePicker } from "@/components/ui/date-range-picker";

export type FilterType = "select" | "input" | "date" | "dateRange" | "text" | "number" | "numberRange" | "datePicker";

export interface FilterOption {
  label: string;
  value: string | number;
}

export interface FilterConfig {
  key: string;
  label: string;
  type: FilterType;
  placeholder?: string;
  hidden?: boolean;
  options?: FilterOption[];
  min?: number;
  max?: number;
  step?: number;
  prefix?: string;
  render?: (
    value: any,
    onChange: (value: any) => void
  ) => React.ReactNode;
}

interface AdvanceFiltersModalProps {
  isOpen: boolean;
  title?: string;
  filters: FilterConfig[];
  initialValues?: Record<string, any>;
  onApply: (values: Record<string, any>) => void;
  onClose: () => void;
  onClearAll?: () => void;
}

export default function AdvanceFiltersModal({
  isOpen,
  title = "Filter",
  filters,
  initialValues = {},
  onApply,
  onClose,
  onClearAll,
}: AdvanceFiltersModalProps) {
  const [values, setValues] = useState<Record<string, any>>({});

  useEffect(() => {
    if (!isOpen) return;

    const defaults: Record<string, any> = {};
    filters.forEach((f) => {
      if (initialValues[f.key] !== undefined) {
        defaults[f.key] = initialValues[f.key];
      } else {
        if (f.type === "dateRange") defaults[f.key] = { from: "", to: "" };
        else if (f.type === "datePicker") defaults[f.key] = undefined;
        else if (f.type === "numberRange") {
          defaults[f.key] = {
            min: f.min ?? 0,
            max: f.max ?? 100,
          };
        } else defaults[f.key] = "";
      }
    });
    setValues(defaults);
  }, [filters, initialValues, isOpen]);

  if (!isOpen) return null;

  const handleChange = (key: string, value: any) => {
    setValues((prev) => ({ ...prev, [key]: value }));
  };

  const handleApply = () => {
    onApply(values);
    onClose();
  };

  const handleClear = () => {
    const cleared: Record<string, any> = {};
    filters.forEach((f) => {
      if (f.type === "dateRange") cleared[f.key] = { from: "", to: "" };
      else if (f.type === "datePicker") cleared[f.key] = undefined;
      else if (f.type === "numberRange") {
        cleared[f.key] = {
          min: f.min ?? 0,
          max: f.max ?? 100,
        };
      } else cleared[f.key] = "";
    });
    setValues(cleared);
    onClearAll?.();
    onClose?.();
  };

  return (
    <div
      className="fixed inset-0 z-50 flex justify-end bg-black/30"
      role="dialog"
      aria-modal="true"
      aria-labelledby="filter-title"
      onClick={(e) => {
        e.stopPropagation();
        onClose();
      }}
    >
      <div
        className="w-[320px] h-full bg-[var(--primary-background-color)] shadow-lg flex flex-col"
        onClick={(e) => { e.stopPropagation(); }}
      >
        <div className="flex items-center justify-between px-4 h-[44px] border-b border-[var(--ui-border-color)]">
          <h2 id="filter-title" className="text-[15px] font-semibold text-[var(--primary-text-color)]">
            {title}
          </h2>
          <div className="flex items-center gap-2">
            <Button
              type="button"
              onClick={handleClear}
              className="text-[13px] px-2 h-[32px] hover:bg-[#F1F2F4] text-[var(--secondary-text-color)] bg-transparent"
            >
              Clear
            </Button>
            <span className="text-[13px] text-[var(--secondary-text-color)]">|</span>
            <Button onClick={onClose} className="hover:bg-[#F1F2F4] p-1 h-[32px] w-[32px] rounded-full bg-transparent">
              <X className="w-5 h-5 text-[var(--secondary-text-color)]" />
            </Button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4">
          {filters
            .filter((f) => !f.hidden)
            .map((filter) => {
              const value = values[filter.key];

              if (filter.render) {
                return (
                  <div key={filter.key} className="flex flex-col gap-1">
                    <label className="text-[13px] font-medium text-[var(--primary-text-color)]">
                      {filter.label}
                    </label>
                    {filter.render(value, (v) => handleChange(filter.key, v))}
                  </div>
                );
              }

              switch (filter.type) {
                case "select":
                  return (
                    <div key={filter.key} className="flex flex-col gap-1">
                      <label className="text-[13px] font-medium text-[var(--primary-text-color)]">
                        {filter.label}
                      </label>
                      <Select
                        value={value || undefined}
                        onValueChange={(val) => handleChange(filter.key, val)}
                      >
                        <SelectTrigger className="h-[36px] rounded-[4px] border border-[var(--ui-border-color)] px-2 text-[13px] text-[var(--secondary-text-color)]">
                          <SelectValue placeholder={filter.placeholder || "Select Option"} />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectGroup>
                            {filter.options?.map((opt) => (
                              <SelectItem key={opt.value} value={String(opt.value)}>
                                {opt.label}
                              </SelectItem>
                            ))}
                          </SelectGroup>
                        </SelectContent>
                      </Select>
                    </div>
                  );

                case "date":
                  return (
                    <div key={filter.key} className="flex flex-col gap-1">
                      <Label htmlFor={filter.key} className="text-[13px] font-medium text-[var(--primary-text-color)]">
                        {filter.label}
                      </Label>
                      <Input
                        id={filter.key}
                        type="date"
                        className="h-[36px] rounded-[4px] border border-[var(--ui-border-color)] px-2 text-[13px] text-[var(--secondary-text-color)] w-full"
                        value={value || ""}
                        onChange={(e) => handleChange(filter.key, e.target.value)}
                      />
                    </div>
                  );

                case "text":
                  return (
                    <div key={filter.key} className="flex flex-col gap-1">
                      <label className="text-[13px] font-medium text-[var(--primary-text-color)]">
                        {filter.label}
                      </label>
                      <textarea
                        className="min-h-[60px] rounded-[4px] border border-[var(--ui-border-color)] px-2 py-1 text-[13px] text-[var(--secondary-text-color)]"
                        placeholder={filter.placeholder}
                        value={value || ""}
                        onChange={(e) => handleChange(filter.key, e.target.value)}
                      />
                    </div>
                  );

                case "number":
                  return (
                    <div key={filter.key} className="flex flex-col gap-1">
                      <label className="text-[13px] font-medium text-[var(--primary-text-color)]">
                        {filter.label}
                      </label>
                      <input
                        type="number"
                        inputMode="decimal"
                        className="h-[36px] rounded-[4px] border border-[var(--ui-border-color)] px-2 text-[13px] text-[var(--secondary-text-color)]"
                        placeholder={filter.placeholder}
                        value={value || ""}
                        onChange={(e) => handleChange(filter.key, e.target.value)}
                      />
                    </div>
                  );

                case "numberRange": {
                  const rangeMin = filter.min ?? 0;
                  const rangeMax = filter.max ?? 100;
                  const step = filter.step ?? 1;
                  const safeValue = value ?? { min: rangeMin, max: rangeMax };
                  const currentMin = typeof safeValue.min === "number" ? safeValue.min : Number(safeValue.min || rangeMin);
                  const currentMax = typeof safeValue.max === "number" ? safeValue.max : Number(safeValue.max || rangeMax);
                  const prefix = filter.prefix ?? "";

                  return (
                    <div key={filter.key} className="flex flex-col gap-2">
                      <Label htmlFor="slider" className="text-[13px] font-medium text-[var(--primary-text-color)]">
                        {filter.label}
                      </Label>
                      <Slider
                        id="slider"
                        min={rangeMin}
                        max={rangeMax}
                        step={step}
                        value={[currentMin, currentMax]}
                        onValueChange={(vals) =>
                          handleChange(filter.key, { min: vals[0], max: vals[1] })
                        }
                      />
                      <div className="flex justify-between text-[12px] text-[var(--secondary-text-color)]">
                        <span>{prefix}{currentMin}</span>
                        <span>{prefix}{currentMax}</span>
                      </div>
                    </div>
                  );
                }

                case "dateRange":
                  return (
                    <div key={filter.key} className="flex flex-col gap-1">
                      <Label htmlFor={filter.key} className="text-[13px] font-medium text-[var(--primary-text-color)]">
                        {filter.label}
                      </Label>
                      <DateRangePicker value={value} onChange={(r) => handleChange(filter.key, r)} />
                    </div>
                  );

                case "input":
                default:
                  return (
                    <div key={filter.key} className="flex flex-col gap-1">
                      <label className="text-[13px] font-medium text-[var(--primary-text-color)]">
                        {filter.label}
                      </label>
                      <input
                        type="text"
                        className="h-[36px] rounded-[4px] border border-[var(--ui-border-color)] px-2 text-[13px] text-[var(--secondary-text-color)]"
                        placeholder={filter.placeholder}
                        value={value || ""}
                        onChange={(e) => handleChange(filter.key, e.target.value)}
                      />
                    </div>
                  );
              }
            })}
        </div>

        <div className="border-t border-[var(--ui-border-color)] px-4 py-3 flex justify-end items-center">
          <div className="flex gap-2">
            <button
              type="button"
              onClick={onClose}
              className="h-[32px] px-4 text-[13px] rounded-[4px] bg-[#ffffff] hover:bg-[#F1F2F4] text-[var(--secondary-text-color)]"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleApply}
              className="h-[32px] px-4 text-[13px] rounded-[4px] bg-[#B71010] text-white"
            >
              Apply
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
